import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import LayoutHeader from "../../../../../layout/LayoutHeader";
import { setFocusProduct } from "../../../../../store/focuspage/focuspageSlice";
import "../../../../../css/ColorPicker.css";

// Utility function to convert HSL to Hex
const hslToHex = (h, s, l) => {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = n => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0'); // Convert to hex
    };
    return `#${f(0)}${f(8)}${f(4)}`;
};

// Add hex validation function
const isValidHexChar = (char) => {
    return /^[0-9A-Fa-f]$/.test(char);
};

export default function Step4() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    let product = useSelector(state => state.focuspage.product);
    
    const [hue, setHue] = useState(0);
    const [saturation, setSaturation] = useState(100);
    const [brightness, setBrightness] = useState(50);
    const [inputValue, setInputValue] = useState("");
    const [validationMessage, setValidationMessage] = useState("");
    const [isValid, setIsValid] = useState(false);
    
    // Reference for the color field and context
    const canvasRef = React.useRef(null);
    
    // Initialize from existing color if present
    useEffect(() => {
        if (product?.accent) {
            setIsValid(true);
            // Try to convert the hex accent color to HSL
            const hex = product.accent.startsWith('#') ? product.accent : `#${product.accent}`;
            const rgb = hexToRgb(hex);
            if (rgb) {
                const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
                setHue(hsl.h);
                setSaturation(hsl.s);
                setBrightness(hsl.l);
                updateColorField(hsl.h);
            }
        }
    }, []);
    
    // Function to handle hue changes
    const handleHueChange = (e) => {
        setHue(e.target.value);
        setInputValue(''); // Clear input value to show live hex updates
        updateColorField(e.target.value);
    };

    // Function to update the 2D color field based on hue
    const updateColorField = (hueValue) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        
        // Clear the canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Create horizontal gradient (saturation: 0% to 100%)
        const gradientH = ctx.createLinearGradient(0, 0, canvas.width, 0);
        gradientH.addColorStop(0, `hsl(${hueValue}, 0%, 50%)`); // Left: gray (0% saturation)
        gradientH.addColorStop(1, `hsl(${hueValue}, 100%, 50%)`); // Right: full saturation
        
        ctx.fillStyle = gradientH;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Create vertical gradient (brightness: 0% to 100%)
        const gradientV = ctx.createLinearGradient(0, canvas.height, 0, 0);
        gradientV.addColorStop(0, "rgba(0, 0, 0, 1)"); // Bottom: black (0% brightness)
        gradientV.addColorStop(0.5, "rgba(0, 0, 0, 0)"); // Middle: no effect
        gradientV.addColorStop(1, "rgba(255, 255, 255, 1)"); // Top: white (100% brightness)
        
        ctx.fillStyle = gradientV;
        ctx.globalCompositeOperation = 'overlay'; // Better blend mode for color picking
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    };
    
    // Handle click or drag on color field
    const handleColorFieldClick = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        
        // Get mouse position relative to canvas
        const x = Math.max(0, Math.min(canvas.width, e.clientX - rect.left));
        const y = Math.max(0, Math.min(canvas.height, e.clientY - rect.top));
        
        // Calculate saturation (x-axis, 0-100)
        const s = Math.round((x / canvas.width) * 100);
        
        // Calculate brightness (y-axis, 0-100)
        // In Figma's model, top is 100% brightness, bottom is 0%
        const l = Math.round((1 - y / canvas.height) * 100);
        
        setSaturation(s);
        setBrightness(l);
        setInputValue('');
    };
    
    // Initialize color field on component mount and when hue changes
    React.useEffect(() => {
        updateColorField(hue);
    }, [hue]);
    
    const handleHexInput = (e) => {
        let value = e.target.value.toUpperCase();
        
        // Remove non-hex characters
        value = value.replace(/[^0-9A-F#]/g, '');
        
        // Ensure # is at the start
        if (!value.startsWith('#') && value !== '') {
            value = '#' + value;
        }
        
        setInputValue(value);
        
        // Only update color if we have a valid hex code
        if (/^#[0-9A-F]{6}$/.test(value)) {
            const rgb = hexToRgb(value);
            const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
            
            // Update sliders based on HSL values
            setHue(hsl.h);
            setSaturation(hsl.s);
            setBrightness(hsl.l);
            
            // Make sure to update the color field
            updateColorField(hsl.h);
        }
    };
    
    // Create a new function to handle vertical slider touch
    const handleVerticalSliderTouch = (e) => {
        // Get the coordinates
        const sliderContainer = e.currentTarget;
        const rect = sliderContainer.getBoundingClientRect();
        
        // Get the touch or click position
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
        
        // Convert to hue (0-360), INVERTED: top is 360, bottom is 0
        const newHue = Math.round(360 - (y / rect.height) * 360);
        
        // Update the state and color field
        setHue(newHue);
        updateColorField(newHue);
        setInputValue('');
    };
    
    // Add this effect to completely disable scrolling
    React.useEffect(() => {
        // Save original body styles
        const originalStyle = window.getComputedStyle(document.body).overflow;
        
        // Disable scrolling on mount
        document.body.style.overflow = 'hidden';
        
        // Re-enable scrolling on unmount
        return () => {
            document.body.style.overflow = originalStyle;
        };
    }, []);
    
    // Calculating the current color using HSL
    const selectedColor = `hsl(${hue}, ${saturation}%, ${brightness}%)`;
    const hexColor = hslToHex(hue, saturation, brightness);
    
    // Update Redux whenever the selected color changes
    React.useEffect(() => {
        const colorValue = hexColor.replace("#", "");
        dispatch(setFocusProduct({ accent: colorValue }));
        setIsValid(true);
    }, [hue, saturation, brightness, dispatch, hexColor]);

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <p className="text-white">Select secondary color</p>
                        <div className="flex gap-[30px]">
                            <div
                                className={`text-white cursor-pointer ${isValid ? "opacity-100" : "opacity-50"}`}
                                onClick={() => isValid && navigate("/focus-page/product-type/step5")}
                            >
                                Next
                            </div>
                            <div 
                                className="text-white cursor-pointer"
                                onClick={() => navigate("/focus-page/product-type/step3")}
                            >
                                Go back
                            </div>
                        </div>
                    </div>

                    <div className="w-full px-4">
                        <div className='flex justify-between items-start gap-x-[15px] flex-nowrap'>
                            {/* 2D Color Field */}
                            <div 
                                style={{
                                    position: 'relative',
                                    width: '280px',
                                    height: '260px',
                                    maxWidth: '85%',
                                    flexShrink: 1,
                                    touchAction: 'none' // Prevent scrolling
                                }}
                            >
                                <canvas 
                                    ref={canvasRef}
                                    width={280}
                                    height={260}
                                    style={{
                                        cursor: 'crosshair',
                                        border: '0px solid rgba(255, 255, 255, 0.2)',
                                        width: '100%',
                                        height: '100%'
                                    }}
                                    onClick={handleColorFieldClick}
                                    onMouseDown={(e) => {
                                        handleColorFieldClick(e);
                                        
                                        const handleMouseMove = (moveEvent) => {
                                            handleColorFieldClick(moveEvent);
                                        };
                                        
                                        const handleMouseUp = () => {
                                            document.removeEventListener('mousemove', handleMouseMove);
                                            document.removeEventListener('mouseup', handleMouseUp);
                                        };
                                        
                                        document.addEventListener('mousemove', handleMouseMove);
                                        document.addEventListener('mouseup', handleMouseUp);
                                    }}
                                    onTouchStart={(e) => {
                                        e.preventDefault(); // Prevent scrolling
                                        const touch = e.touches[0];
                                        const touchEvent = {
                                            clientX: touch.clientX,
                                            clientY: touch.clientY
                                        };
                                        handleColorFieldClick(touchEvent);
                                    }}
                                    onTouchMove={(e) => {
                                        e.preventDefault(); // Prevent scrolling
                                        const touch = e.touches[0];
                                        const touchEvent = {
                                            clientX: touch.clientX,
                                            clientY: touch.clientY
                                        };
                                        handleColorFieldClick(touchEvent);
                                    }}
                                />
                                <div 
                                    style={{
                                        position: 'absolute',
                                        width: '10px',
                                        height: '10px',
                                        border: '2px solid white',
                                        borderRadius: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        pointerEvents: 'none',
                                        left: `${Math.min((saturation / 100) * 280, 280)}px`,
                                        top: `${(1 - brightness / 100) * 260}px`
                                    }}
                                />
                            </div>
                            
                            {/* Vertical Hue Slider */}
                            <div 
                                style={{
                                    position: 'relative',
                                    height: '260px',
                                    width: '20px',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onTouchStart={handleVerticalSliderTouch}
                                onTouchMove={handleVerticalSliderTouch}
                                onMouseDown={handleVerticalSliderTouch}
                            >
                                <div style={{
                                    position: 'absolute',
                                    transform: 'rotate(-90deg) translate(-130px, 0)',
                                    transformOrigin: 'left center',
                                    width: '260px',
                                    height: '20px'
                                }}>
                                    <input
                                        type="range"
                                        min="0"
                                        max="360"
                                        value={hue}
                                        onChange={handleHueChange}
                                        className="hue-slider"
                                        style={{ width: '260px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex mt-6 items-center gap-[10px]">
                            <span className="text-white text-uppercase text-[12px] leading-[18px] h-[18px]">HEX</span>
                            <input
                                className="bg-transparent border-none text-white w-[70px] text-uppercase font-inherit text-[12px] leading-[18px] h-[18px]"
                                value={inputValue || hexColor.toUpperCase()}
                                onChange={handleHexInput}
                                maxLength={7}
                                placeholder="#000000"
                                style={{ outline: 'none' }}
                            />
                            <span 
                                className="w-[16px] h-[16px]" 
                                style={{ 
                                    backgroundColor: selectedColor, 
                                    border: '1px solid white', 
                                    borderRadius: '4px' 
                                }}
                            ></span>
                        </div>
                    </div>

                    <div className="w-full px-4 absolute bottom-[27px] left-0">
                        <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5]">
                            <p>Choose a color that will complement your main color.</p>
                            <p>This will be used for accents and highlights throughout.</p>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
}

// Utility functions for color conversion
const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
};

const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default: break;
        }
        h /= 6;
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
    };
};

